import { z } from 'zod';

// Playlist URL validation — accept either a full URL or a bare PL... id.
// The route layer parses URLs via parseYouTubePlaylistUrl from
// lib/youtube-fetch and rejects unrecognized formats with 400.
const playlistInputSchema = z
  .string()
  .trim()
  .min(8)
  .max(2048)
  .refine(
    (v) => {
      // Looks like a bare playlist id (PL..., UU..., LL..., etc.) — YT format
      // is alnum+_- and 18–34 chars typically.
      if (/^[A-Za-z0-9_-]{8,128}$/.test(v)) return true;
      // Else try to parse as URL.
      try {
        const u = new URL(v);
        return u.protocol === 'https:' || u.protocol === 'http:';
      } catch {
        return false;
      }
    },
    { message: 'ใส่ลิงก์ YouTube playlist หรือ playlist ID' },
  );

// Mirror the taxonomies slug regex (ไทย + Latin lowercase + digits,
// dash-separated, no leading/trailing dash, no consecutive dashes).
// Lets the public URL show readable Thai segments — admin doesn't have
// to think up a Latin alias just to satisfy the form. Same pattern as
// categories/tags, so reviewers don't need to track a 2nd shape.
const slugSchema = z
  .string()
  .trim()
  .min(1, 'ใส่ slug')
  .max(80)
  .regex(/^[฀-๿a-z0-9]+(?:-[฀-๿a-z0-9]+)*$/u, 'slug ใช้ได้: ไทย / a-z / 0-9 และ - เท่านั้น');

/* ---------- Playlist CRUD ---------- */

export const createYoutubePlaylistSchema = z.object({
  name: z.string().trim().min(1, 'ใส่ชื่อ').max(100),
  /** Optional — auto-derived from name if omitted via slugify(). */
  slug: slugSchema.optional(),
  /** Either a full YT playlist URL or a bare playlist id. */
  playlistInput: playlistInputSchema,
  description: z.string().max(500).optional().nullable(),
  fetchIntervalMinutes: z.coerce.number().int().min(5).max(1440).default(720),
  isActive: z.boolean().default(true),
  sortOrder: z.coerce.number().int().min(0).max(9999).default(0),
});

export const updateYoutubePlaylistSchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  slug: slugSchema.optional(),
  description: z.string().max(500).optional().nullable(),
  fetchIntervalMinutes: z.coerce.number().int().min(5).max(1440).optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.coerce.number().int().min(0).max(9999).optional(),
  /** Optional — if provided AND differs from the current playlist_id, the
   *  backend re-parses, updates the row, AND deletes all existing videos
   *  (they belonged to a different upstream playlist). */
  playlistInput: playlistInputSchema.optional(),
});

/* ---------- Video CRUD ---------- */

// Same shape as playlistInputSchema (accept URL or bare id) but the bounds
// match a YouTube video id (11 chars) too.
const videoInputSchema = z
  .string()
  .trim()
  .min(8)
  .max(2048)
  .refine(
    (v) => {
      if (/^[A-Za-z0-9_-]{8,128}$/.test(v)) return true;
      try {
        const u = new URL(v);
        return u.protocol === 'https:' || u.protocol === 'http:';
      } catch {
        return false;
      }
    },
    { message: 'ใส่ลิงก์ YouTube video หรือ video ID' },
  );

export const addYoutubeVideoSchema = z.object({
  videoInput: videoInputSchema,
});

export const updateYoutubeVideoSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().max(5000).optional().nullable(),
  thumbnailUrl: z.string().url().max(2048).optional(),
  position: z.coerce.number().int().min(0).max(9999).optional(),
  isHidden: z.boolean().optional(),
});

export type CreateYoutubePlaylistInput = z.infer<typeof createYoutubePlaylistSchema>;
export type UpdateYoutubePlaylistInput = z.infer<typeof updateYoutubePlaylistSchema>;
export type AddYoutubeVideoInput = z.infer<typeof addYoutubeVideoSchema>;
export type UpdateYoutubeVideoInput = z.infer<typeof updateYoutubeVideoSchema>;
